import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { formatMinutes, parseDurationToMinutes } from '../lib/format';
import { Button, ErrorBanner } from '../ui';
import { Modal } from '../ui/dialog';
import { useToast } from '../ui/toast';
import { CHARGE_TYPE_LABELS, type ChargeType } from '../types';

// The fast path: log time from anywhere in the fewest possible decisions. No timesheet
// is chosen — the entry lands in the right period bucket automatically. With exactly one
// technician the field disappears entirely (the one-person shop never sees it).

export function QuickLog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();

  const technicians = useQuery({ queryKey: ['technicians'], queryFn: () => api.listTechnicians(), enabled: open });
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.listClients(), enabled: open });

  const techs = technicians.data?.data ?? [];
  const soloTech = techs.length === 1 ? techs[0] : null;

  const [techId, setTechId] = useState('');
  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [duration, setDuration] = useState('');
  const [chargeType, setChargeType] = useState<ChargeType>('tm');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const minutes = parseDurationToMinutes(duration);
  const effectiveTech = soloTech?.id ?? techId;
  const canLog = !!effectiveTech && minutes != null && minutes > 0;

  const log = useMutation({
    mutationFn: () =>
      api.createTimeEntry({
        technician_id: effectiveTech,
        ...(clientId ? { client_id: clientId } : {}),
        occurred_on: date,
        minutes: minutes!,
        charge_type: chargeType,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: (entry) => {
      setDuration('');
      setNote('');
      setError(null);
      onClose();
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      qc.invalidateQueries({ queryKey: ['timesheets', 'paged'] });
      qc.invalidateQueries({ queryKey: ['timesheet', entry.timesheet_id] });
      qc.invalidateQueries({ queryKey: ['unbilled-time'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
      toast({
        title: `Logged ${formatMinutes(entry.minutes)}`,
        detail: clientId ? undefined : 'No client set — assign one before billing.',
      });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="Log time" width={460}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (canLog) log.mutate();
        }}
      >
        {!soloTech && (
          <div className="field">
            <label htmlFor="ql-tech">Technician</label>
            <select id="ql-tech" value={techId} onChange={(e) => setTechId(e.target.value)}>
              <option value="">Select…</option>
              {techs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="ql-client">Client</label>
          <select id="ql-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">No client (internal)</option>
            {clients.data?.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {!clientId && chargeType === 'tm' && (
            <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
              Without a client, this time can’t be invoiced until one is assigned.
            </span>
          )}
        </div>
        <div className="form-row">
          <div className="field">
            <label htmlFor="ql-duration">Time</label>
            <input
              id="ql-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="1:30, 90m, or 1.5h"
              autoFocus
            />
            {duration.trim() !== '' && (
              <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
                {minutes != null ? `= ${formatMinutes(minutes)}` : 'Try “1:30”, “90m”, or “1.5h”'}
              </span>
            )}
          </div>
          <div className="field">
            <label htmlFor="ql-date">Date</label>
            <input id="ql-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="ql-charge">Charge type</label>
          <select id="ql-charge" value={chargeType} onChange={(e) => setChargeType(e.target.value as ChargeType)}>
            {(Object.keys(CHARGE_TYPE_LABELS) as ChargeType[]).map((c) => (
              <option key={c} value={c}>
                {CHARGE_TYPE_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="ql-note">
            Note <span className="muted">(becomes the invoice line)</span>
          </label>
          <input id="ql-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was the work?" />
        </div>
        {error && <ErrorBanner error={error} />}
        <div className="modal-actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={log.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canLog || log.isPending}>
            {log.isPending ? 'Logging…' : minutes != null ? `Log ${formatMinutes(minutes)}` : 'Log time'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
